/** Datos mínimos para la descripción amigable (wallet y listados contables). */
export interface WalletTransactionDescriptionInput {
  type?: string;
  description?: string;
  reference?: { type: string; id: number } | null;
  related?: unknown;
}

const formatDate = (dateString?: string): string => {
  if (!dateString) return '';

  try {
    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) return '';

    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();

    return `${day}/${month}/${year}`;
  } catch {
    return '';
  }
};

const getRelatedRecord = (related: unknown): Record<string, unknown> | null => {
  if (!related) return null;
  if (Array.isArray(related)) {
    return related.length > 0 ? (related[0] as Record<string, unknown>) : null;
  }
  if (typeof related === 'object') return related as Record<string, unknown>;
  return null;
};

export const getTransactionDescription = (
  transaction: WalletTransactionDescriptionInput,
  emptyValueLabel: string,
): { text: string; link?: string } => {
  const type = (transaction.type ?? '').toUpperCase();
  const relatedData = getRelatedRecord(transaction.related);
  const reference = transaction.reference;

  const str = (v: unknown) => (typeof v === 'string' ? v : '');
  const optStr = (key: string) => str(relatedData?.[key]);

  switch (type) {
    case 'INVOICE': {
      const erpNum = optStr('erp_number') || optStr('erp_num');
      if (erpNum) {
        const link = reference?.id ? `/invoices/view/${reference.id}` : undefined;
        return {
          text: `Factura ${erpNum}`,
          link,
        };
      }
      if (reference?.id) {
        return {
          text: transaction.description || `Factura #${reference.id}`,
          link: `/invoices/view/${reference.id}`,
        };
      }
      break;
    }

    case 'COD_PAYOUT':
    case 'PAYOUT': {
      const payoutDate = optStr('payout_date') || optStr('date') || optStr('created_at') || optStr('paid_at');

      if (payoutDate) {
        const formattedDate = formatDate(payoutDate);
        const link = reference?.id ? `/cod-payments/payouts/${reference.id}` : undefined;
        return {
          text: `Liquidación de reembolsos ${formattedDate}`,
          link,
        };
      }
      if (reference?.id) {
        return {
          text: transaction.description || `Liquidación de reembolsos #${reference.id}`,
          link: `/cod-payments/payouts/${reference.id}`,
        };
      }
      break;
    }

    case 'WITHDRAWAL':
      if (optStr('bank_transfer_date')) {
        const formattedDate = formatDate(optStr('bank_transfer_date'));
        return {
          text: `Transferencia ${formattedDate}`,
        };
      }
      break;

    case 'WITHDRAWAL_RETURN':
      if (optStr('bank_transfer_date')) {
        const formattedDate = formatDate(optStr('bank_transfer_date'));
        return {
          text: `Devolución transferencia ${formattedDate}`,
        };
      }
      break;

    case 'TOPUP':
      if (optStr('transfer_date')) {
        const formattedDate = formatDate(optStr('transfer_date'));
        return {
          text: `Recarga saldo ${formattedDate}`,
        };
      }
      break;
  }

  return {
    text: transaction.description || emptyValueLabel,
  };
};
